import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { getStateList } from "../../services/StateService";
import { getCountryList } from "../../services/CountryService";
import TimePicker from "../../components/Common/Timepicker";
import { getTimezoneList } from "../../services/EnumService";
import { handleConsoleError } from "../../utils/common-methods";
import { toast } from "react-toastify";

const EventLocationDetails = ({
  register,
  errors,
  setEventData,
  eventData,
  setValue,
  getValues,
  setError,
  clearErrors,
  watch,
  step,
  props,
}) => {
  const [stateList, setStateList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [timezoneList, setTimezoneList] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedTimezoneId, setSelectedTimezoneId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [isCountrySelected, setIsCountrySelected] = useState(false);
  const today = new Date();
  const tomorrow = new Date(today.setHours(0, 0, 0, 0));
  tomorrow.setDate(tomorrow.getDate() + 1);

  const validateTimeRange = () => {
    const startDate = new Date(getValues("startDate")).setHours(0, 0, 0, 0);
    const endDate = new Date(getValues("endDate")).setHours(0, 0, 0, 0);

    const parseTime = (timeStr) => {
      const [time, ampm] = timeStr?.split(" ");
      const [hour, minute] = time?.split(":")?.map(Number);
      return {
        hour:
          ampm === "PM" && hour !== 12
            ? hour + 12
            : ampm === "AM" && hour === 12
            ? 0
            : hour,
        minute,
      };
    };

    if (eventData.startTimeStr && eventData.endTimeStr) {
      const { hour: startHour, minute: startMinute } = parseTime(
        eventData.startTimeStr
      );
      const { hour: endHour, minute: endMinute } = parseTime(
        eventData.endTimeStr
      );

      const startDateTime = new Date(startDate).setHours(
        startHour,
        startMinute
      );
      const endDateTime = new Date(endDate).setHours(endHour, endMinute);

      if (startDate === endDate && endDateTime <= startDateTime) {
        setError("endTimeStr", {
          message:
            "End Time must be greater than Start Time when Start Date equals End Date",
        });
      } else {
        clearErrors("endTimeStr");
      }
    }
  };
  const filteredStates = stateList.filter(
    (state) => state.countryId === selectedCountryId
  );
  const handleTimeChange = (field, value) => {
    // setEventData((prev) => ({ ...prev, [field]: value }));
    eventData[field] = value;
    setValue(field, value, { shouldValidate: true });
    // Check required validation for start and end time
    if (!value && step === 2) {
      setError(field, {
        message: `${
          field === "startTimeStr" ? "Start" : "End"
        } Time is required`,
      });
    } else {
      clearErrors(field);
    }

    validateTimeRange();
  };
  const setSelectedCountry = (value) => {
    if (value) {
      setSelectedCountryId(value);
    } else {
      setSelectedCountryId("");
      setSelectedStateId("");
    }
  };
  const setSelectedState = (value) => {
    if (value) {
      setSelectedStateId(value);
    } else {
      setSelectedStateId("");
    }
  };
  const getEventData = async () => {
    try {
      props.application.setLoading(true);
      setSelectedTimezoneId(eventData.timeZoneId);
      const stateList = await getStateList();
      const countryList = await getCountryList();
      const timezoneList = await getTimezoneList();
      setStateList(stateList.data || []);
      setCountryList(countryList.data || []);
      setTimezoneList(timezoneList.data || []);
      if (eventData.countryId) {
        setSelectedCountryId(eventData?.countryId);
        setEventData((prev) => ({
          ...prev,
          country: countryList.data.find(
            (country) => country.id === eventData.countryId
          ),
        }));
      }
      if (eventData.stateId) {
        setSelectedStateId(eventData.stateId);
        setEventData((prev) => ({
          ...prev,
          state: stateList.data.find((state) => state.id === eventData.stateId),
        }));
      }
      props.application.setLoading(false);
    } catch (error) {
      props.application.setLoading(false);
     const message= handleConsoleError(error);
      toast.error(error?.message|| message);
    }
  };
  useEffect(() => {
    getEventData();
    const subscription = watch((values, { name }) => {
      if (name === "startDate" || name === "endDate") {
        validateTimeRange(); // Re-run time range validation when dates change
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);
  return (
    <React.Fragment>
      <div>
        <h2>Location & Date</h2>

        <Form.Group controlId="country" className="mb-3">
          <Form.Label>
            State<span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="select"
            {...register("countryId", {
              required: "State is required",
            })}
            value={selectedCountryId}
            onChange={(e) => {
              const countryId = e.target.value;
              setSelectedCountry(e.target.value);
              setIsCountrySelected(countryId !== "");
            }}
          >
            <option value="">Select State</option>
            {countryList.map((country) => (
              <option key={country?.id} value={country?.id}>
                {country?.countryName}
              </option>
            ))}
          </Form.Control>
          {errors.countryId && (
            <span className="text-danger">{errors.countryId?.message}</span>
          )}
        </Form.Group>

        <Form.Group controlId="street1" className="mb-3">
          <Form.Label>Street 1</Form.Label>
          <Form.Control
            maxLength={100}
            type="text"
            {...register("street1")}
            onChange={(e) => {
              const value = e.target.value;
              setValue(
                "street1",
                value?.charAt(0)?.toUpperCase() + value?.slice(1)
              );
            }}
          />
          {errors.street1 && (
            <span className="text-danger">{errors.street1?.message}</span>
          )}
        </Form.Group>
        <Form.Group controlId="street2" className="mb-3">
          <Form.Label>Street 2</Form.Label>
          <Form.Control
            maxLength={100}
            type="text"
            {...register("street2")}
            onChange={(e) => {
              const value = e.target.value;
              setValue(
                "street2",
                value?.charAt(0)?.toUpperCase() + value?.slice(1)
              );
            }}
          />
          {errors.street2 && (
            <span className="text-danger">{errors.street2?.message}</span>
          )}
        </Form.Group>

        <Form.Group controlId="city" className="mb-3">
          <Form.Label>City</Form.Label>
          <Form.Control
            maxLength={50}
            type="text"
            {...register("city", {
              required: isCountrySelected ? "City is required" : false,
            })}
            onChange={(e) => {
              const value = e.target.value;
              setValue("city", value?.charAt(0)?.toUpperCase() + value?.slice(1));
            }}
          />
          {errors.city && (
            <span className="text-danger">{errors.city?.message}</span>
          )}
        </Form.Group>

        <Form.Group controlId="state" className="mb-3">
          <Form.Label>Country</Form.Label>
          <Form.Control
            as="select"
            {...register("stateId", {
              required: isCountrySelected ? "Country is required" : false,
            })}
            value={selectedStateId}
            onChange={(e) => {
              setSelectedStateId(e.target.value);
              setSelectedState(e.target.value);
            }}
          >
            <option value="">Select Country</option>
            {filteredStates.map((state) => (
              <option key={state.id} value={state.id}>
                {state.stateName}
              </option>
            ))}
          </Form.Control>
          {errors.stateId && (
            <span className="text-danger">{errors.stateId?.message}</span>
          )}
        </Form.Group>

        <Form.Group controlId="zipCode" className="mb-3">
          <Form.Label>Zip/Postal Code</Form.Label>
          <Form.Control
            maxLength={15}
            type="text"
            {...register("zip", {
              required: isCountrySelected ? "Zip Code is required" : false,
              pattern: {
                value: /^[A-Za-z0-9\s-]{3,10}$/,
                message: "Invalid Postal Code",
              },
            })}
            defaultValue={eventData?.zipCode}
          />
          {errors.zip && (
            <span className="text-danger">{errors.zip?.message}</span>
          )}
        </Form.Group>
        <Form.Group controlId="startDate" className="mb-3">
          <Form.Label>
            Event Start Date<span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            {...register("startDate", {
              required: "Start Date is required",
              validate: (value) => {
                const selectedDate = new Date(value).setHours(0, 0, 0, 0);
                return (
                  selectedDate >= tomorrow ||
                  "Start Date must be greater than today's date"
                );
              },
            })}
            defaultValue={eventData?.startDate}
            min={tomorrow.toISOString().split("T")[0]}
          />
          {errors.startDate && (
            <span className="text-danger">{errors.startDate?.message}</span>
          )}
        </Form.Group>

        <TimePicker
          label="Event Start Time"
          name="startTimeStr"
          value={eventData.startTimeStr}
          onChange={(value) => handleTimeChange("startTimeStr", value)}
          error={errors.startTimeStr?.message}
        />

        <Form.Group controlId="endDate" className="mb-3">
          <Form.Label>
            Event End Date<span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            {...register("endDate", {
              required: "End Date is required",
              validate: (value) => {
                const startDate = new Date(getValues("startDate")).setHours(
                  0,
                  0,
                  0,
                  0
                );
                const endDate = new Date(value).setHours(0, 0, 0, 0);
                return (
                  endDate >= startDate ||
                  "End Date must be greater than or equal to Start Date"
                );
              },
            })}
            defaultValue={eventData.endDate}
            min={watch("startDate") || new Date().toISOString().split("T")[0]}
          />
          {errors.endDate && (
            <span className="text-danger">{errors.endDate?.message}</span>
          )}
        </Form.Group>

        <TimePicker
          label="Event End Time"
          name="endTimeStr"
          value={eventData.endTimeStr}
          onChange={(value) => handleTimeChange("endTimeStr", value)}
          error={errors.endTimeStr?.message}
        />
        {/* <Form.Group controlId="timeZoneId">
          <Form.Label>
            Timezone<span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="select"
            {...register("timeZoneId", {
              required: "Timezone is required",
            })}
            value={selectedTimezoneId}
            onChange={(e) => {
              setSelectedTimezoneId(e.target.value);
            }}
          >
            <option value="">Select Timezone</option>
            {timezoneList.map((timezone) => (
              <option key={timezone.id} value={timezone.id}>
                {timezone.name}
              </option>
            ))}
          </Form.Control>
          {errors.timeZoneId && (
            <span className="text-danger">{errors.timeZoneId.message}</span>
          )}
        </Form.Group> */}
      </div>
    </React.Fragment>
  );
};

export default EventLocationDetails;
